import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, X, UserCog, Power, PowerOff } from "lucide-react";

const ROLES = ["operations_admin", "trust_safety_admin", "support_admin", "finance_admin", "content_admin", "developer_admin"];

export default function AdminStaff() {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = async () => { try { setRows((await api.get("/admin/staff")).data.rows); } catch (e) {} };
  useEffect(() => { load(); }, []);
  const setActive = async (uid, active) => {
    try { await api.patch(`/admin/staff/${uid}`, { active }); toast.success(active ? "Activated" : "Deactivated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (uid, email) => {
    if (!confirm(`Remove ${email} from staff?`)) return;
    try { await api.delete(`/admin/staff/${uid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const changeRole = async (uid, staff_role) => {
    try { await api.patch(`/admin/staff/${uid}`, { staff_role }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <AdminLayout subtitle="Module 26 · Ops" title="Super Admin Staff Management" actions={
      <Button onClick={() => setShowNew(true)} className="rounded-sm bg-ec-emerald text-ec-ink gap-2" data-testid="staff-new"><Plus className="w-4 h-4" />New staff admin</Button>
    }>
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="staff-table">
            <thead className="bg-white/5">
              <tr className="text-left">
                <th className="px-4 py-3 eyebrow text-zinc-500">Name</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Email</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Role</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Status</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Created</th>
                <th className="px-4 py-3 eyebrow text-zinc-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`staff-row-${s.id}`}>
                  <td className="px-4 py-3 font-semibold text-white">{s.full_name}</td>
                  <td className="px-4 py-3 text-zinc-300">{s.email}</td>
                  <td className="px-4 py-3">
                    <select value={s.staff_role || "operations_admin"} onChange={(e) => changeRole(s.id, e.target.value)} className="rounded-sm h-8 px-2 bg-white/5 border border-white/10 text-white text-xs" data-testid={`staff-role-${s.id}`}>
                      {ROLES.map((r) => <option key={r} value={r} className="bg-ec-ink">{r}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">{s.suspended ? <Badge className="bg-ec-error/20 text-ec-error border-ec-error/40 rounded-sm">Inactive</Badge> : <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/40 rounded-sm">Active</Badge>}</td>
                  <td className="px-4 py-3 text-xs font-mono-plex text-zinc-400">{s.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => setActive(s.id, !!s.suspended)} className="p-1.5 border border-white/10 rounded-sm text-zinc-300 hover:bg-white/10" data-testid={`staff-toggle-${s.id}`}>{s.suspended ? <Power className="w-3 h-3" /> : <PowerOff className="w-3 h-3" />}</button>
                      <button onClick={() => del(s.id, s.email)} className="p-1.5 border border-ec-error/30 text-ec-error rounded-sm hover:bg-ec-error/10" data-testid={`staff-delete-${s.id}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-zinc-500" data-testid="staff-empty">No staff admins yet — invite your operations / trust & safety / finance team.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {showNew && <StaffModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </AdminLayout>
  );
}

function StaffModal({ onClose, onSaved }) {
  const [f, setF] = useState({ email: "", full_name: "", staff_role: "operations_admin", password: "", note: "" });
  const save = async () => {
    try { await api.post("/admin/staff", f); toast.success("Staff added"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="staff-modal">
      <div className="bg-ec-ink border border-white/10 rounded-sm max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-xl font-bold text-white flex items-center gap-2"><UserCog className="w-5 h-5 text-ec-emerald" />New staff admin</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div><Label className="eyebrow text-zinc-500">Full name</Label><Input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white mt-2" data-testid="staff-input-name" /></div>
          <div><Label className="eyebrow text-zinc-500">Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white mt-2" data-testid="staff-input-email" /></div>
          <div><Label className="eyebrow text-zinc-500">Temporary password</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white mt-2" data-testid="staff-input-password" /></div>
          <div><Label className="eyebrow text-zinc-500">Role</Label>
            <select value={f.staff_role} onChange={(e) => setF({ ...f, staff_role: e.target.value })} className="rounded-sm h-10 px-3 bg-white/5 border border-white/10 text-white text-sm w-full mt-2" data-testid="staff-input-role">
              {ROLES.map((r) => <option key={r} value={r} className="bg-ec-ink">{r}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" className="rounded-sm border-white/10 text-white" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="rounded-sm bg-ec-emerald text-ec-ink" data-testid="staff-save">Create staff admin</Button>
        </div>
      </div>
    </div>
  );
}
